/**
 * PDF Export — server-side rendering via WeasyPrint.
 *
 * Flow:
 * 1. Clone the message DOM elements
 * 2. Capture any chart SVGs as PNG images via html2canvas (charts are the
 *    only elements that need rasterization; everything else is HTML text)
 * 3. Assemble a clean HTML string with a header and message blocks
 * 4. POST the HTML to the backend /export-pdf endpoint
 * 5. WeasyPrint renders it to a real, selectable-text PDF with full Unicode
 * 6. Download the returned PDF blob directly — no print dialog
 */

import html2canvas from 'html2canvas';
import { createAuthHeaders } from '../auth/authHeaders.js';

/* ── API base (mirrors api.js) ─────────────────────────────────────── */

function getApiBase() {
  const explicit = process.env.REACT_APP_SIGMUND_API_BASE;
  if (explicit && explicit.trim()) return explicit.trim();
  const prefix = process.env.REACT_APP_SIGMUND_API_PREFIX || '/sigmund/api';
  const devBackend = process.env.REACT_APP_BACKEND_URL_DEV || 'http://127.0.0.1:8000';
  const prodBackend = process.env.REACT_APP_BACKEND_URL_PROD || '';
  if (process.env.NODE_ENV === 'development') return `${devBackend}${prefix}`;
  if (prodBackend) return `${prodBackend}${prefix}`;
  return prefix;
}

const API_BASE = getApiBase();

/* ── Chart capture ─────────────────────────────────────────────────── */

async function captureChartAsBase64(el) {
  // Temporarily force dark text on the ORIGINAL element so html2canvas
  // reads the correct computed styles (onclone was unreliable).
  const saved = [];

  // SVG text/tspan elements (axes, tick values, legends)
  el.querySelectorAll('text, tspan').forEach(t => {
    saved.push({ node: t, fill: t.getAttribute('fill'), styleFill: t.style.fill });
    t.setAttribute('fill', '#1a1a2a');
    t.style.setProperty('fill', '#1a1a2a', 'important');
  });

  // SVG line/path elements for axis lines
  el.querySelectorAll('.recharts-cartesian-axis line, .recharts-cartesian-axis path').forEach(l => {
    saved.push({ node: l, stroke: l.getAttribute('stroke'), styleStroke: l.style.stroke });
    l.setAttribute('stroke', '#94a3b8');
    l.style.setProperty('stroke', '#94a3b8', 'important');
  });

  // Grid lines → subtle gray
  el.querySelectorAll('.recharts-cartesian-grid line').forEach(l => {
    saved.push({ node: l, stroke: l.getAttribute('stroke'), styleStroke: l.style.stroke, isGrid: true });
    l.setAttribute('stroke', '#e2e8f0');
  });

  // HTML legend text — CSS has !important so we must use setProperty with important
  el.querySelectorAll('.recharts-legend-item-text').forEach(t => {
    saved.push({ node: t, color: t.style.color, isLegend: true });
    t.style.setProperty('color', '#1a1a2a', 'important');
  });

  // Chart title
  el.querySelectorAll('.message__chart-title').forEach(t => {
    saved.push({ node: t, color: t.style.color, isTitle: true });
    t.style.setProperty('color', '#0f172a', 'important');
  });

  // Store original background
  const origBg = el.style.background;
  el.style.background = '#ffffff';

  try {
    const canvas = await html2canvas(el, {
      scale: 2.5,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
    });
    return canvas.toDataURL('image/png');
  } catch (err) {
    console.error('Chart capture failed:', err);
    return null;
  } finally {
    // Restore all original values
    el.style.background = origBg;
    for (const s of saved) {
      if (s.isLegend || s.isTitle) {
        s.node.style.color = s.color || '';
      } else if (s.isGrid) {
        if (s.stroke !== null) s.node.setAttribute('stroke', s.stroke);
        else s.node.removeAttribute('stroke');
      } else if ('stroke' in s) {
        if (s.stroke !== null) s.node.setAttribute('stroke', s.stroke);
        else s.node.removeAttribute('stroke');
        s.node.style.stroke = s.styleStroke || '';
      } else {
        if (s.fill !== null) s.node.setAttribute('fill', s.fill);
        else s.node.removeAttribute('fill');
        s.node.style.fill = s.styleFill || '';
      }
    }
  }
}

/* ── HTML assembly ─────────────────────────────────────────────────── */

async function buildHtml(articles) {
  const dateStr = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const header = `<div class="pdf-header">
    <span class="pdf-header__brand">Sigmund</span>
    <span class="pdf-header__date">${dateStr}</span>
  </div>`;

  const blocks = [];

  for (const article of articles) {
    const isUser = article.classList.contains('user');
    const label = isUser ? 'You' : 'Sigmund';
    const cls = isUser ? 'msg--user' : 'msg--ai';

    const bodyEl = article.querySelector('.message__body');
    const clone = (bodyEl || article).cloneNode(true);

    // Strip UI-only elements
    clone.querySelectorAll('.message__download-btn, .agent-thinking, .agent-actions').forEach(n => n.remove());

    // Capture charts from the ORIGINAL element (for proper sizing),
    // then replace the cloned chart element with an <img>
    const origCharts = Array.from((bodyEl || article).querySelectorAll('.message__chart'));
    const cloneCharts = Array.from(clone.querySelectorAll('.message__chart'));

    for (let i = 0; i < origCharts.length; i++) {
      const b64 = await captureChartAsBase64(origCharts[i]);
      if (b64 && cloneCharts[i]) {
        const img = document.createElement('img');
        img.src = b64;
        img.className = 'chart-img';
        img.alt = 'Chart';
        cloneCharts[i].replaceWith(img);
      } else if (cloneCharts[i]) {
        cloneCharts[i].innerHTML = '<p style="color:#94a3b8;font-style:italic;">[Chart]</p>';
      }
    }

    blocks.push(
      `<div class="msg ${cls}">
        <div class="msg__label">${label}</div>
        <div class="msg__content">${clone.innerHTML}</div>
      </div>`
    );
  }

  return header + '\n' + blocks.join('\n');
}

/* ── POST to backend & download ────────────────────────────────────── */

async function downloadPdf(articles, filename) {
  if (!articles || articles.length === 0) {
    window.alert('No content to export.');
    return;
  }

  try {
    const html = await buildHtml(articles);

    const response = await fetch(`${API_BASE}/export-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html, filename })
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `Server returned ${response.status}`);
    }

    // Download the PDF blob
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('PDF export failed:', err);
    window.alert('PDF export failed: ' + (err.message || err));
  }
}

/* ── Public API ────────────────────────────────────────────────────── */

export async function exportMessageAsPdf(messageElement, index) {
  await downloadPdf([messageElement], `sigmund-response-${index + 1}.pdf`);
}

export async function exportConversationAsPdf(messagesContainerElement) {
  const articles = Array.from(messagesContainerElement.querySelectorAll('article.message'));
  const today = new Date().toISOString().slice(0, 10);
  await downloadPdf(articles, `sigmund-conversation-${today}.pdf`);
}
